import {
  AccountPerformanceSellInConfig,
  defaultProductImageMissingVisualConfig,
  defaultRecommendationsPanelsConfig,
  defaultScannerConfiguration,
  IScannerConfiguration,
  ISearchNewsConfig,
  ProductImageMissingVisualConfig,
  PropertyPathSvgReplacement,
  pulseConfigurationDefaultValues,
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
  UserFeedbackPagesConfig,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import { IElementsStylesConfig } from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import {
  RecommendationsPanels,
  RecommendationsPanelsConfig,
} from '@epicuro-next/models/recommendation-panels';
import { isNumber, isString } from '@epicuro-next/utilities';
import { createSelector, MemoizedSelector } from '@ngrx/store';
import { BasketNotificationStyle } from 'libs/constants/rule-engine/pulse-rules/src/lib/types/basket-notification-style';

import { selectConfigurationValueFrom } from './configuration.selectors';

export const selectIsAccountAddressReverted = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.AccountAddressReverted,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.AccountAddressReverted,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.AccountAddressReverted
    ],
  ),
  Boolean,
);

export const selectAccountReviewersV2Enabled = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.AccountReviewersV2Enabled,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.AccountReviewersV2Enabled,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.AccountReviewersV2Enabled
    ],
  ),
  Boolean,
);

/** Undefined until the namespace loads, so an unresolved rule keeps the epicuro endpoints. */
export const selectUseBasketsReviewV2Enabled = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.UseBasketsReviewV2,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.UseBasketsReviewV2,
    pulseConfigurationDefaultValues[PulseConfigurationKeys.UseBasketsReviewV2],
  ),
  Boolean,
);

export const selectAccountPerformanceFeedbackEnabled = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.AccountPerformanceFeedbackEnabled,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.AccountPerformanceFeedbackEnabled,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.AccountPerformanceFeedbackEnabled
    ],
  ),
  Boolean,
);

export const selectDiscountFormatRule = selectConfigurationValueFrom<
  PulseConfigurationKeys.discountFormat,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.discountFormat,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.discountFormat],
) as MemoizedSelector<object, string>;

export const selectShowMaterialWidthIds = selectConfigurationValueFrom<
  PulseConfigurationKeys.ShowMaterialWidthIds,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.ShowMaterialWidthIds,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.ShowMaterialWidthIds],
) as MemoizedSelector<object, boolean>;

export const selectAccountSelectorBackgroundImage =
  selectConfigurationValueFrom<
    PulseConfigurationKeys.AccountSelectorBackgroundImage,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.AccountSelectorBackgroundImage,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.AccountSelectorBackgroundImage
    ],
  ) as MemoizedSelector<object, string>;

export const selectEnableUseragreementButtonsAtEOD =
  selectConfigurationValueFrom<
    PulseConfigurationKeys.EnableUseragreementButtonsAtEOD,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.EnableUseragreementButtonsAtEOD,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.EnableUseragreementButtonsAtEOD
    ],
  ) as MemoizedSelector<object, boolean>;

export const selectTermsConditionsUserconsent = selectConfigurationValueFrom<
  PulseConfigurationKeys.TermsConditionsUserconsent,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.TermsConditionsUserconsent,
  pulseConfigurationDefaultValues[
    PulseConfigurationKeys.TermsConditionsUserconsent
  ],
) as MemoizedSelector<object, boolean>;

export const selectIsCatalogOnlyModeRule = selectConfigurationValueFrom<
  PulseConfigurationKeys.EnableCatalogOnlyMode,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.EnableCatalogOnlyMode,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.EnableCatalogOnlyMode],
);

export const selectEnabledSearchViews = selectConfigurationValueFrom<
  PulseConfigurationKeys.EnabledSearchViews,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.EnabledSearchViews,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.EnabledSearchViews],
);

export const selectTrackingEventsEnabledRule = selectConfigurationValueFrom<
  PulseConfigurationKeys.TrackingEventsEnabled,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.TrackingEventsEnabled,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.TrackingEventsEnabled],
);

export const selectIsFiltersVisibleRule = selectConfigurationValueFrom<
  PulseConfigurationKeys.EnableFiltersVisible,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.EnableFiltersVisible,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.EnableFiltersVisible],
);

export const selectEnableProductSearchPdfExport = selectConfigurationValueFrom<
  PulseConfigurationKeys.EnableProductSearchPdfExport,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.EnableProductSearchPdfExport,
  pulseConfigurationDefaultValues[
    PulseConfigurationKeys.EnableProductSearchPdfExport
  ],
);

export const selectShowWspDefaultValueRule = selectConfigurationValueFrom<
  PulseConfigurationKeys.ShowWholeSalePricesSettingsDefaultValue,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.ShowWholeSalePricesSettingsDefaultValue,
  pulseConfigurationDefaultValues[
    PulseConfigurationKeys.ShowWholeSalePricesSettingsDefaultValue
  ],
);

export const selectShowAccountInHeader = selectConfigurationValueFrom<
  PulseConfigurationKeys.ShowAccountInHeader,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.ShowAccountInHeader,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.ShowAccountInHeader],
);

export const selectShowAccountTooltipInHeader = selectConfigurationValueFrom<
  PulseConfigurationKeys.ShowAccountTooltipInHeader,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.ShowAccountTooltipInHeader,
  pulseConfigurationDefaultValues[
    PulseConfigurationKeys.ShowAccountTooltipInHeader
  ],
);

export const selectUpdateTitleOptionsRule = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.UpdateTitleOptions,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.UpdateTitleOptions,
    pulseConfigurationDefaultValues[PulseConfigurationKeys.UpdateTitleOptions],
  ),
  (configuration) => {
    try {
      return JSON.parse(configuration as string);
    } catch {
      return pulseConfigurationDefaultValues[
        PulseConfigurationKeys.UpdateTitleOptions
      ];
    }
  },
);

export const selectContactsUserMenu = selectConfigurationValueFrom<
  PulseConfigurationKeys.ContactsUserMenu,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.ContactsUserMenu,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.ContactsUserMenu],
);

export const selectInvoicesPageEnabledRule = selectConfigurationValueFrom<
  PulseConfigurationKeys.InvoicesPageEnabled,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.InvoicesPageEnabled,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.InvoicesPageEnabled],
) as MemoizedSelector<object, boolean>;

export const selectNotificationPreferencesEnabledRule =
  selectConfigurationValueFrom<
    PulseConfigurationKeys.NotificationPreferencesEnabled,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.NotificationPreferencesEnabled,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.NotificationPreferencesEnabled
    ],
  ) as MemoizedSelector<object, boolean>;

export const selectHighlightFutureTypeLinesEnabled = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.HighlightFutureTypeLinesEnabled,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.HighlightFutureTypeLinesEnabled,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.HighlightFutureTypeLinesEnabled
    ],
  ),
  Boolean,
);

export const selectPreventVisualSearchInEdge = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.PreventVisualSearchInEdge,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.PreventVisualSearchInEdge,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.PreventVisualSearchInEdge
    ],
  ),
  Boolean,
);

export const selectProductImageMissingVisual = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.ProductImageMissingVisual,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.ProductImageMissingVisual,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.ProductImageMissingVisual
    ],
  ),
  (setting) => {
    try {
      if (!setting) {
        return defaultProductImageMissingVisualConfig;
      }

      const configFromString = JSON.parse(
        setting,
      ) as Partial<ProductImageMissingVisualConfig>;

      const enabledKey: keyof ProductImageMissingVisualConfig = 'enabled';
      const enabled =
        enabledKey in configFromString
          ? Boolean(configFromString.enabled)
          : defaultProductImageMissingVisualConfig.enabled;

      const replacementInstructionsKey: keyof ProductImageMissingVisualConfig =
        'replacementInstructions';
      const propertyPathKey: keyof PropertyPathSvgReplacement = 'propertyPath';
      const propertyFindgKey: keyof PropertyPathSvgReplacement = 'findInSvg';
      const replacementInstructions =
        replacementInstructionsKey in configFromString &&
        Array.isArray(configFromString.replacementInstructions)
          ? configFromString.replacementInstructions.filter(
              (ri) =>
                propertyPathKey in ri &&
                isString(ri.propertyPath) &&
                propertyFindgKey in ri &&
                isString(ri.findInSvg),
            )
          : defaultProductImageMissingVisualConfig.replacementInstructions;

      const svgKey: keyof ProductImageMissingVisualConfig = 'svg';
      const svg =
        svgKey in configFromString && isString(configFromString.svg)
          ? configFromString.svg
          : defaultProductImageMissingVisualConfig.svg;

      const thumbnailZoomFactorKey: keyof ProductImageMissingVisualConfig =
        'thumbnailZoomFactor';
      const thumbnailZoomFactor =
        thumbnailZoomFactorKey in configFromString &&
        (isNumber(configFromString.thumbnailZoomFactor) ||
          (isString(configFromString.thumbnailZoomFactor) &&
            configFromString.thumbnailZoomFactor))
          ? Number.parseFloat(
              (configFromString.thumbnailZoomFactor as number | string)
                .toString()
                .trim(),
            )
          : defaultProductImageMissingVisualConfig.thumbnailZoomFactor;

      const config: ProductImageMissingVisualConfig = {
        enabled,
        replacementInstructions,
        svg,
        thumbnailZoomFactor,
      };

      return config;
    } catch (e) {
      console.warn(
        `Invalid value found for rule '${PulseConfigurationKeys.ProductImageMissingVisual}', falling back to default settings`,
      );

      return defaultProductImageMissingVisualConfig;
    }
  },
);

export const selectRecommendationsPanelsRule = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.RecommendationsPanels,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.RecommendationsPanels,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.RecommendationsPanels
    ],
  ),
  (recommendationsPanelsConfigValue: unknown): RecommendationsPanelsConfig => {
    const defaultConfig = JSON.parse(
      JSON.stringify(defaultRecommendationsPanelsConfig),
    ) as RecommendationsPanelsConfig;

    try {
      if (!isString(recommendationsPanelsConfigValue)) {
        console.warn(
          `Invalid type (${typeof recommendationsPanelsConfigValue}) used to define rule '${
            PulseConfigurationKeys.RecommendationsPanels
          }', falling back to default settings`,
        );

        return defaultConfig;
      }

      const config = {
        ...defaultConfig,
      };
      const objFromConfig = JSON.parse(recommendationsPanelsConfigValue);

      if (typeof objFromConfig === 'object' && objFromConfig !== null) {
        const keysFromConfig = Object.keys(objFromConfig);
        const validKeys = Object.keys(config);
        for (const key of keysFromConfig) {
          const validKey = validKeys.find(
            (vk) => vk.toLowerCase() === key.toLowerCase(),
          ) as keyof typeof RecommendationsPanels | undefined;

          if (validKey === undefined) {
            console.warn(`${key} is not a valid key for RecommendationsPanels`);
          } else {
            config[validKey] = objFromConfig[key] ?? [];
          }
        }

        return config;
      }
    } catch (e) {
      console.warn(
        `Invalid value found for rule '${PulseConfigurationKeys.RecommendationsPanels}', falling back to default settings`,
      );

      return defaultConfig;
    }

    return defaultConfig;
  },
);

/**
 * Tri-state on purpose: `undefined` until the pulse namespace has loaded, so
 * consumers can tell "rules not resolved yet" apart from "rule is off" —
 * coercing early with `Boolean` would report the kill switch as off during
 * the initial rules fetch.
 */
export const selectSpotlightSlotReservationEnabled = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.SpotlightSlotReservationEnabled,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.SpotlightSlotReservationEnabled,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.SpotlightSlotReservationEnabled
    ],
  ),
  (value) => (value === undefined ? undefined : Boolean(value)),
);

export const selectPowerListEnabled = selectConfigurationValueFrom<
  PulseConfigurationKeys.PowerListEnabled,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.PowerListEnabled,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.PowerListEnabled],
);

export const selectRecommendationSlotsFromMerchandiser =
  selectConfigurationValueFrom<
    PulseConfigurationKeys.RecommendationSlotsFromMerchandiser,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.RecommendationSlotsFromMerchandiser,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.RecommendationSlotsFromMerchandiser
    ],
  );

export const selectUseHeartIconForBookmark = selectConfigurationValueFrom<
  PulseConfigurationKeys.UseHeartIconForBookmark,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.UseHeartIconForBookmark,
  pulseConfigurationDefaultValues[
    PulseConfigurationKeys.UseHeartIconForBookmark
  ],
);

export const selectSearchNewsConfigRule = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.SearchNewsConfig,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.SearchNewsConfig,
    pulseConfigurationDefaultValues[PulseConfigurationKeys.SearchNewsConfig],
  ),
  (config): ISearchNewsConfig => {
    try {
      return JSON.parse(config as string) as ISearchNewsConfig;
    } catch (e) {
      console.warn(`Failed to parse search news config: ${e}`);
      return {};
    }
  },
);

export const selectDisableVideoContextMenu = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.DisableVideoContextMenu,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.DisableVideoContextMenu,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.DisableVideoContextMenu
    ],
  ),
  Boolean,
);

export const selectEnableShareBrowserApi = selectConfigurationValueFrom<
  PulseConfigurationKeys.EnableShareBrowserApi,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.EnableShareBrowserApi,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.EnableShareBrowserApi],
);

export const selectBookmarkIcon = createSelector(
  selectUseHeartIconForBookmark,
  (useHeart) => {
    return useHeart ? ['far', 'heart'] : ['far', 'bookmark'];
  },
);

export const selectBookmarkedIcon = createSelector(
  selectUseHeartIconForBookmark,
  (useHeart) => {
    return useHeart ? ['fas', 'heart'] : ['fas', 'bookmark'];
  },
);

export const selectBasketNotificationStyle = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.BasketNotificationStyle,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.BasketNotificationStyle,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.BasketNotificationStyle
    ],
  ),
  (basketNotificationStyle) => {
    const allValidStyles = Object.values(BasketNotificationStyle).map(
      (v) => v as string,
    );

    if (basketNotificationStyle) {
      const value = basketNotificationStyle.trim().toLowerCase();
      if (allValidStyles.includes(value)) {
        return value;
      }
    }

    console.warn(
      `BasketNotificationStyle '${basketNotificationStyle}' is not a valid value. Valid values are: ${allValidStyles.join(
        ', ',
      )}. Falling back to '${BasketNotificationStyle.default}'.`,
    );

    return BasketNotificationStyle.default;
  },
);

export const selectGcrNormalizePriceForPurchaseEvent =
  selectConfigurationValueFrom<
    PulseConfigurationKeys.GcrNormalizePriceForPurchaseEvent,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.GcrNormalizePriceForPurchaseEvent,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.GcrNormalizePriceForPurchaseEvent
    ],
  );

export const selectAttributesPriorityConfig = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.AttributesPriorityConfig,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.AttributesPriorityConfig,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.AttributesPriorityConfig
    ],
  ),
  (config) => {
    try {
      return JSON.parse(config as string);
    } catch (e) {
      console.error(`Failed provided rule attributes-priority-config: ${e}`);
      return JSON.parse(
        pulseConfigurationDefaultValues[
          PulseConfigurationKeys.AttributesPriorityConfig
        ],
      );
    }
  },
);

export const selectUserMenuBadgesConfig = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.UserMenuItemsBadges,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.UserMenuItemsBadges,
    pulseConfigurationDefaultValues[PulseConfigurationKeys.UserMenuItemsBadges],
  ),
  (config) => {
    try {
      return JSON.parse(config as string);
    } catch (e) {
      console.error(
        `Failed provided rule user-menu-items-badges: ${e}. Use format: { "e2eKey": { "type": "danger", "text": "new" } }`,
      );
      return JSON.parse(
        pulseConfigurationDefaultValues[
          PulseConfigurationKeys.UserMenuItemsBadges
        ],
      );
    }
  },
);

export const selectElementsStylesConfig = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.ElementDynamicStyles,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.ElementDynamicStyles,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.ElementDynamicStyles
    ],
  ),
  (config): IElementsStylesConfig => {
    try {
      return JSON.parse(config as string) as IElementsStylesConfig;
    } catch (e) {
      console.error(`Failed provided rule dynamic-element-styles: ${e}`);
      return JSON.parse(
        pulseConfigurationDefaultValues[
          PulseConfigurationKeys.ElementDynamicStyles
        ],
      );
    }
  },
);

export const selectQuickSearchDropdown = selectConfigurationValueFrom<
  PulseConfigurationKeys.QuickSearchDropdown,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.QuickSearchDropdown,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.QuickSearchDropdown],
);

export const selectSearchSuggestEnabled = selectConfigurationValueFrom<
  PulseConfigurationKeys.SearchSuggestEnabled,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.SearchSuggestEnabled,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.SearchSuggestEnabled],
);

export const selectShowMerchandiserChatbot = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.ShowMerchandiserChatbot,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.ShowMerchandiserChatbot,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.ShowMerchandiserChatbot
    ],
  ),
  Boolean,
);

export const selectShowMarkupEnabled = selectConfigurationValueFrom<
  PulseConfigurationKeys.ShowMarkup,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.ShowMarkup,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.ShowMarkup],
);

export const selectProductIdAdditionalPrefixRule = selectConfigurationValueFrom<
  PulseConfigurationKeys.ProductIdAdditionalPrefix,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.ProductIdAdditionalPrefix,
  pulseConfigurationDefaultValues[
    PulseConfigurationKeys.ProductIdAdditionalPrefix
  ],
);

export const selectProductIdAdditionalPrefixRexExp = createSelector(
  selectProductIdAdditionalPrefixRule,
  (additionalPrefix) => new RegExp(additionalPrefix ?? '', 'gi'),
);

export const selectToggleDiscountLabelByWsp = selectConfigurationValueFrom<
  PulseConfigurationKeys.ToggleDiscountLabelByWsp,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.ToggleDiscountLabelByWsp,
  pulseConfigurationDefaultValues[
    PulseConfigurationKeys.ToggleDiscountLabelByWsp
  ],
);

export const selectHideAsideMenu = selectConfigurationValueFrom<
  PulseConfigurationKeys.HideAsideMenu,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.HideAsideMenu,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.HideAsideMenu],
);

export const selectTooltipToShow = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.TooltipToShow,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.TooltipToShow,
    pulseConfigurationDefaultValues[PulseConfigurationKeys.TooltipToShow],
  ),
  (config) => {
    try {
      return JSON.parse(config as string);
    } catch (e) {
      console.error(`Failed provided rule tooltip to show: ${e}`);
      return JSON.parse(
        pulseConfigurationDefaultValues[PulseConfigurationKeys.TooltipToShow],
      );
    }
  },
);

export const selectEnableOnboardingAddon = selectConfigurationValueFrom<
  PulseConfigurationKeys.EnableOnboardingAddon,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.EnableOnboardingAddon,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.EnableOnboardingAddon],
);

export const selectTrueDemandEnabledRule = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.TrueDemandEnabled,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.TrueDemandEnabled,
    pulseConfigurationDefaultValues[PulseConfigurationKeys.TrueDemandEnabled],
  ),
  Boolean,
);

export const selectAccountPerformancePageEnabled = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.AccountPerformancePageEnabled,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.AccountPerformancePageEnabled,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.AccountPerformancePageEnabled
    ],
  ),
  Boolean,
);

export const selectAccountPerformancePageSlot = selectConfigurationValueFrom<
  PulseConfigurationKeys.AccountPerformancePageSlot,
  PulseConfigurations
>(PULSE_FEATURE_NAME, PulseConfigurationKeys.AccountPerformancePageSlot, '');

/**
 * Stays `undefined` until the pulse rules namespace is loaded, so a guard can wait for the real value.
 */
export const selectAccountPerformanceWidgetEngineEnabled =
  selectConfigurationValueFrom<
    PulseConfigurationKeys.AccountPerformanceWidgetEngineEnabled,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.AccountPerformanceWidgetEngineEnabled,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.AccountPerformanceWidgetEngineEnabled
    ],
  );

const SELL_IN_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(T.*)?$/;

/**
 * Validates the sell-in rule and warns on anything that is not a usable
 * `{ date, labelKey }` pair, so a bad admin edit hides the banner instead of
 * showing a broken date.
 */
export const selectAccountPerformanceSellInRule = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.AccountPerformanceSellIn,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.AccountPerformanceSellIn,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.AccountPerformanceSellIn
    ],
  ),
  (sellInConfigValue: unknown): AccountPerformanceSellInConfig | null => {
    const sellInObject = readSellInObject(sellInConfigValue);
    return sellInObject === null ? null : toSellInConfig(sellInObject);
  },
);

function readSellInObject(
  sellInConfigValue: unknown,
): Record<string, unknown> | null {
  if (sellInConfigValue === undefined || sellInConfigValue === null) {
    return null;
  }
  if (
    typeof sellInConfigValue === 'object' &&
    !Array.isArray(sellInConfigValue)
  ) {
    return sellInConfigValue as Record<string, unknown>;
  }
  if (!isString(sellInConfigValue)) {
    console.warn(
      `Invalid type (${typeof sellInConfigValue}) used to define rule '${
        PulseConfigurationKeys.AccountPerformanceSellIn
      }', hiding the sell-in banner`,
    );
    return null;
  }
  const trimmedConfigValue = sellInConfigValue.trim();
  if (trimmedConfigValue === '') {
    return null;
  }
  const parsedSellInObject = parseSellInJson(trimmedConfigValue);
  if (parsedSellInObject === null) {
    console.warn(
      `Invalid value found for rule '${PulseConfigurationKeys.AccountPerformanceSellIn}', hiding the sell-in banner`,
    );
  }
  return parsedSellInObject;
}

function parseSellInJson(sellInJson: string): Record<string, unknown> | null {
  try {
    const parsedConfigValue: unknown = JSON.parse(sellInJson);
    if (
      typeof parsedConfigValue === 'object' &&
      parsedConfigValue !== null &&
      !Array.isArray(parsedConfigValue)
    ) {
      return parsedConfigValue as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

function toSellInConfig(
  sellInObject: Record<string, unknown>,
): AccountPerformanceSellInConfig | null {
  const { date, labelKey } = sellInObject;
  const trimmedDate = isString(date) ? date.trim() : '';
  const trimmedLabelKey = isString(labelKey) ? labelKey.trim() : '';
  if (
    trimmedDate === '' ||
    trimmedLabelKey === '' ||
    !isSellInDateText(trimmedDate)
  ) {
    console.warn(
      `Invalid value found for rule '${PulseConfigurationKeys.AccountPerformanceSellIn}', hiding the sell-in banner`,
    );
    return null;
  }
  return { date: trimmedDate, labelKey: trimmedLabelKey };
}

function isSellInDateText(dateText: string): boolean {
  const match = SELL_IN_DATE_PATTERN.exec(dateText);
  if (match === null) {
    return false;
  }
  const [, yearText, monthText, dayText, timePart] = match;
  if (timePart !== undefined && Number.isNaN(Date.parse(dateText))) {
    return false;
  }
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const roundTrippedDate = new Date(Date.UTC(year, month - 1, day));
  return (
    roundTrippedDate.getUTCFullYear() === year &&
    roundTrippedDate.getUTCMonth() === month - 1 &&
    roundTrippedDate.getUTCDate() === day
  );
}

export const selectPasswordMinLength = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.PasswordMinLength,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.PasswordMinLength,
    pulseConfigurationDefaultValues[PulseConfigurationKeys.PasswordMinLength],
  ),
  (value) =>
    isNumber(value) && value > 0
      ? value
      : pulseConfigurationDefaultValues[
          PulseConfigurationKeys.PasswordMinLength
        ],
);

export const selectUserFeedbackPagesConfig = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.UserFeedbackPages,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.UserFeedbackPages,
    pulseConfigurationDefaultValues[PulseConfigurationKeys.UserFeedbackPages],
  ),
  (value: unknown): UserFeedbackPagesConfig => {
    try {
      if (!isString(value)) return {};
      return JSON.parse(value) as UserFeedbackPagesConfig;
    } catch {
      return {};
    }
  },
);

/**
 * The scanner reads one JSON rule rather than a spread of individual ones.
 * Fields are taken only when present and of the right type, so a partial or
 * fat-fingered rule degrades to the defaults field by field instead of
 * dropping the whole configuration.
 */
export const selectScannerConfiguration = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.ScannerConfiguration,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.ScannerConfiguration,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.ScannerConfiguration
    ],
  ),
  (value: unknown): IScannerConfiguration => {
    const raw = parseScannerConfiguration(value);
    return {
      enabled: pickBoolean(raw.enabled, defaultScannerConfiguration.enabled),
      resolution: pickNumber(
        raw.resolution,
        defaultScannerConfiguration.resolution,
      ),
      idleZoom: pickNumber(raw.idleZoom, defaultScannerConfiguration.idleZoom),
      detectZoom: pickNumber(
        raw.detectZoom,
        defaultScannerConfiguration.detectZoom,
      ),
      decodeHeight: pickNumber(
        raw.decodeHeight,
        defaultScannerConfiguration.decodeHeight,
      ),
      decodeInterval: pickNumber(
        raw.decodeInterval,
        defaultScannerConfiguration.decodeInterval,
      ),
      decodeVisibleOnly: pickBoolean(
        raw.decodeVisibleOnly,
        defaultScannerConfiguration.decodeVisibleOnly,
      ),
      debug: pickBoolean(raw.debug, defaultScannerConfiguration.debug),
    };
  },
);

function parseScannerConfiguration(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object') {
    return value as Record<string, unknown>;
  }
  if (!isString(value)) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function pickNumber(value: unknown, fallback: number): number {
  return isNumber(value) && Number.isFinite(value) ? value : fallback;
}

function pickBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

export const selectBulkAgreementsEnabled = selectConfigurationValueFrom<
  PulseConfigurationKeys.BulkAgreementsEnabled,
  PulseConfigurations
>(PULSE_FEATURE_NAME, PulseConfigurationKeys.BulkAgreementsEnabled, false);

/**
 * Routes every check-availability call to the products-search (v2) endpoint
 * instead of the monolith one, regardless of catalog-only mode.
 */
export const selectUseSearchApiForAvailability = createSelector(
  selectConfigurationValueFrom<
    PulseConfigurationKeys.UseSearchApiForAvailability,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.UseSearchApiForAvailability,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.UseSearchApiForAvailability
    ],
  ),
  Boolean,
);
